import { useState } from "react";
import { BarChart3, ChevronDown, ChevronRight } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { fmtTL, fmtNum } from "../../lib/format";

const SERIES = [
  { key: "nakdiLimit", name: "Nakdi Limit", color: "#0f172a" },
  { key: "nakdiRisk", name: "Nakdi Risk", color: "#f59e0b" },
  { key: "gayriLimit", name: "Gayrinakdi Limit", color: "#64748b" },
  { key: "gayriRisk", name: "Gayrinakdi Risk", color: "#fbbf24" },
];
const short = (n) => (Math.abs(n) >= 1e6 ? `${fmtNum(n / 1e6, 1)} M` : Math.abs(n) >= 1e3 ? `${fmtNum(n / 1e3, 0)} B` : fmtNum(n, 0));

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-md px-3 py-2 text-xs" data-testid="lr-chart-tooltip">
      <div className="font-bold text-slate-900 mb-1">{label}</div>
      {payload.map((p) => <div key={p.dataKey} className="flex justify-between gap-4"><span style={{ color: p.color }}>{p.name}</span><b className="font-mono">{p.dataKey === "pct" ? `%${fmtNum(p.value, 1)}` : fmtTL(p.value)}</b></div>)}
    </div>
  );
};

// Banka Karşılaştırma: filtrelenmiş kartlar için nakdi/gayrinakdi limit & risk çubukları + doluluk % çizgisi (sağ eksen)
export default function BankCompareChart({ cards }) {
  const [open, setOpen] = useState(true);
  const multiCompany = new Set(cards.map((c) => c.company)).size > 1;
  const data = cards.map((c) => ({
    name: multiCompany ? `${c.bank} · ${c.company}` : c.bank,
    nakdiLimit: c.totals?.limit?.nakdi || 0, nakdiRisk: c.totals?.risk?.nakdi || 0,
    gayriLimit: c.totals?.limit?.gayrinakdi || 0, gayriRisk: c.totals?.risk?.gayrinakdi || 0,
    pct: c.usage_pct == null ? 0 : Math.min(c.usage_pct, 200),
  }));
  if (cards.length < 2) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="lr-compare">
      <button type="button" onClick={() => setOpen(!open)} data-testid="lr-compare-toggle" className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <BarChart3 className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-bold text-slate-900">Banka Karşılaştırma</span>
        <span className="text-[11px] text-slate-500">· {cards.length} kart · nakdi / gayrinakdi limit ve risk yan yana, çizgi = doluluk %</span>
      </button>
      {open && (
        <div className="px-2 pb-3" style={{ height: 320 }} data-testid="lr-compare-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }} barGap={2} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#334155" }} interval={0} angle={data.length > 5 ? -15 : 0} textAnchor={data.length > 5 ? "end" : "middle"} height={data.length > 5 ? 56 : 30} />
              <YAxis yAxisId="tl" tickFormatter={short} tick={{ fontSize: 10, fill: "#64748b" }} width={64} />
              <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v) => `%${v}`} tick={{ fontSize: 10, fill: "#b45309" }} width={44} />
              <Tooltip content={<Tip />} cursor={{ fill: "rgba(245,158,11,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {SERIES.map((s) => <Bar key={s.key} yAxisId="tl" dataKey={s.key} name={s.name} fill={s.color} radius={[3, 3, 0, 0]} maxBarSize={38} />)}
              <Line yAxisId="pct" type="monotone" dataKey="pct" name="Doluluk %" stroke="#b45309" strokeWidth={2} dot={{ r: 4, fill: "#b45309" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
